/**
 * idleSession.ts
 *
 * Framework-free core of the idle auto-logout — all the arithmetic and all the
 * shared state, with no React in it, so the tricky parts are unit-testable.
 *
 * The server is authoritative. EnforceIdleTimeout keeps a "last seen" record and
 * answers 401 SESSION_IDLE_TIMEOUT once the account's window has passed. Everything
 * here exists to give the user a warning and a way out BEFORE that happens, and to
 * stop the app acting on a session that's already gone.
 *
 * Three design points worth understanding before changing anything:
 *
 * 1. The deadline is anchored on the last heartbeat ACK, never on local activity.
 *    Heartbeats are throttled, so local activity is routinely newer than anything
 *    the server knows about. If the countdown were driven by local activity the
 *    client would think it had longer than it does, and the 5s notification poll
 *    would collect a 401 and hard-log-out the user BEFORE the warning appeared.
 *    Anchoring on the ack means the client is at worst one round-trip behind the
 *    server — never ahead. `SAFETY_MARGIN_MS` biases it further so the client
 *    always acts first.
 *
 * 2. Elapsed time is `max(wall, monotonic)`. Wall clock is the only clock that
 *    counts time the machine spent suspended, so a laptop closed for four hours
 *    must expire. But a wall clock moved BACKWARD (NTP correction, user edit)
 *    would otherwise stall the timer forever, which is the dangerous direction —
 *    the monotonic reading absorbs that. Forward jumps expire early, which is
 *    merely annoying and fail-safe.
 *
 * 3. State is shared across tabs through one localStorage record. Activity in any
 *    tab keeps them all alive, they warn together, and exactly one of them claims
 *    the expiry. All access is try/catch-wrapped (Safari private mode, disabled
 *    storage) following the lib/sidebarPrefs.ts convention — with storage
 *    unavailable this degrades to per-tab in-memory tracking rather than throwing.
 */

const RECORD_KEY = 'ftp.idleSession';

/**
 * Expire this far ahead of the computed server deadline, so the client's own
 * teardown (with its warning, its message, and its clean unmount) always beats a
 * background poll collecting a 401.
 */
const SAFETY_MARGIN_MS = 8_000;

/** Don't rewrite the shared record more than this often on activity. */
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;

/**
 * Ceiling on how long activity holds may keep a session alive with no real input
 * (see `hold`). Bounds a hold leaked by a component that never released it.
 */
const HOLD_CEILING_MS = 4 * 60 * 60 * 1000; // 4 hours

export type IdlePhase = 'inactive' | 'active' | 'warn' | 'expired';

interface IdleRecord {
  /** Date.now() when the last heartbeat ack landed. */
  anchorWall: number;
  /** Seconds of window the server granted at that ack. */
  expiresInSeconds: number;
  /** Seconds before expiry the warning should appear. */
  warnSeconds: number;
  /** Date.now() of the most recent real user interaction, in any tab. */
  lastActivityWall: number;
  /** Date.now() of the last heartbeat we sent (not necessarily acked). */
  lastHeartbeatWall: number;
  /** Whose session this is — a mismatch means the record is stale. */
  userId: number;
  accountId: number;
}

/**
 * Monotonic companion to the record's `anchorWall`, kept per-tab.
 *
 * performance.now() is document-scoped, so it's only comparable with an anchor
 * THIS tab wrote. That's the case that matters: the tab the user is in is the one
 * sending heartbeats. When another tab set the anchor we fall back to wall-only
 * and rely on the server as backstop.
 */
let perfAnchor: { wall: number; perf: number } | null = null;

/** Tags currently holding the session open. In-memory only, never persisted. */
const holds = new Set<string>();
let holdsSince: number | null = null;

/** Fallback record when localStorage is unavailable. */
let memoryRecord: IdleRecord | null = null;

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readRecord(): IdleRecord | null {
  if (!hasStorage()) return memoryRecord;

  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(RECORD_KEY);
  } catch {
    raw = null;
  }

  // Nothing readable. Fall back to this tab's copy rather than reporting "not
  // tracking": storage can be present but unwritable (Safari private mode, quota),
  // in which case writeRecord only ever populated memoryRecord and treating that
  // as untracked would silently disable the whole feature.
  if (!raw) return memoryRecord;

  try {
    const parsed = JSON.parse(raw) as Partial<IdleRecord>;
    if (
      typeof parsed?.anchorWall !== 'number' ||
      typeof parsed?.expiresInSeconds !== 'number' ||
      typeof parsed?.userId !== 'number'
    ) {
      return null;
    }
    return {
      anchorWall: parsed.anchorWall,
      expiresInSeconds: parsed.expiresInSeconds,
      warnSeconds: typeof parsed.warnSeconds === 'number' ? parsed.warnSeconds : 60,
      lastActivityWall:
        typeof parsed.lastActivityWall === 'number' ? parsed.lastActivityWall : parsed.anchorWall,
      lastHeartbeatWall:
        typeof parsed.lastHeartbeatWall === 'number' ? parsed.lastHeartbeatWall : parsed.anchorWall,
      userId: parsed.userId,
      accountId: typeof parsed.accountId === 'number' ? parsed.accountId : 0,
    };
  } catch {
    return null;
  }
}

function writeRecord(record: IdleRecord): void {
  memoryRecord = record;
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(RECORD_KEY, JSON.stringify(record));
  } catch {
    // Quota / private mode — memoryRecord still carries this tab.
  }
}

/** Drop the shared record. Called on any session end and on fresh login. */
export function clearIdleRecord(): void {
  memoryRecord = null;
  perfAnchor = null;
  holds.clear();
  holdsSince = null;
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(RECORD_KEY);
  } catch {
    // Nothing useful to do.
  }
}

/**
 * Start (or re-anchor) tracking from a heartbeat ack.
 *
 * Also used at login, where the login response's policy stands in for an ack —
 * the server opened the record at the same moment it minted the token.
 */
export function anchorFromServer(params: {
  expiresInSeconds: number;
  warnSeconds: number;
  userId: number;
  accountId: number;
}): void {
  const now = Date.now();
  const existing = readRecord();
  perfAnchor = { wall: now, perf: nowPerf() };

  writeRecord({
    anchorWall: now,
    expiresInSeconds: params.expiresInSeconds,
    warnSeconds: params.warnSeconds,
    // Preserve a newer activity stamp from another tab.
    lastActivityWall: Math.max(existing?.lastActivityWall ?? 0, now),
    lastHeartbeatWall: now,
    userId: params.userId,
    accountId: params.accountId,
  });
}

function nowPerf(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/**
 * Milliseconds since the anchor, taking whichever clock reports MORE — see the
 * file docblock, point 2.
 */
function elapsedSinceAnchor(record: IdleRecord, now: number): number {
  const wallElapsed = now - record.anchorWall;
  if (perfAnchor && perfAnchor.wall === record.anchorWall) {
    return Math.max(wallElapsed, nowPerf() - perfAnchor.perf);
  }
  return wallElapsed;
}

export interface IdleEvaluation {
  phase: IdlePhase;
  /** Whole seconds until the client-side deadline; 0 once expired. */
  secondsRemaining: number;
  /** True when a heartbeat should be sent now. */
  shouldHeartbeat: boolean;
}

/**
 * Where this session stands right now.
 *
 * Pure apart from reading the shared record and the clocks: given the same record
 * and `now`, the answer is the same in any tab, on any tick. That's why a 1Hz
 * interval is enough even when the browser throttles it to once a minute — a late
 * tick still computes the right answer, just later, and the visibility/focus
 * handlers cover the cases where ticks stop entirely.
 */
export function evaluate(
  now: number = Date.now(),
  identity?: { userId: number; accountId: number },
): IdleEvaluation {
  const record = readRecord();
  if (!record) {
    return { phase: 'inactive', secondsRemaining: 0, shouldHeartbeat: false };
  }

  // A record belonging to a different user or account is stale (account switch,
  // or a second person on the same browser) and must not be inherited.
  if (
    identity &&
    (identity.userId !== record.userId || identity.accountId !== record.accountId)
  ) {
    return { phase: 'inactive', secondsRemaining: 0, shouldHeartbeat: false };
  }

  const windowMs = record.expiresInSeconds * 1000;
  const elapsed = elapsedSinceAnchor(record, now);
  const remainingMs = windowMs - elapsed - SAFETY_MARGIN_MS;
  const warnMs = record.warnSeconds * 1000;

  const activitySinceHeartbeat = record.lastActivityWall > record.lastHeartbeatWall;
  const heartbeatAge = now - record.lastHeartbeatWall;
  const held = hasActiveHolds(now);

  // Two reasons to heartbeat, both requiring something to report:
  //   - the throttle window has passed, or
  //   - we're inside the warning band and have un-reported activity, which must be
  //     flushed before the window closes (otherwise throttled activity turns into
  //     a surprise logout — the failure mode described in point 1).
  const dueByThrottle = heartbeatAge >= record.expiresInSeconds * 1000 / 5;
  const dueBeforeDeadline = remainingMs <= warnMs + SAFETY_MARGIN_MS;
  const shouldHeartbeat =
    (activitySinceHeartbeat || held) && (dueByThrottle || dueBeforeDeadline);

  if (remainingMs <= 0) {
    // A hold (live call, dictation) suppresses expiry, bounded by the ceiling.
    if (held) {
      return { phase: 'active', secondsRemaining: 0, shouldHeartbeat: true };
    }
    return { phase: 'expired', secondsRemaining: 0, shouldHeartbeat: false };
  }

  return {
    phase: remainingMs <= warnMs && !held ? 'warn' : 'active',
    secondsRemaining: Math.max(0, Math.ceil(remainingMs / 1000)),
    shouldHeartbeat,
  };
}

/**
 * Record real user interaction.
 *
 * Throttled, because this is called from capture-phase listeners on every
 * pointerdown/keydown/input/wheel/scroll. Note it moves only `lastActivityWall`,
 * never the deadline — the deadline is the server's to grant.
 *
 * @returns true when the record was written (i.e. the throttle allowed it)
 */
export function noteActivity(now: number = Date.now()): boolean {
  const record = readRecord();
  if (!record) return false;
  if (now - record.lastActivityWall < ACTIVITY_WRITE_THROTTLE_MS) return false;

  writeRecord({ ...record, lastActivityWall: now });
  return true;
}

/** Mark a heartbeat as sent, so the throttle and the flush logic see it. */
export function noteHeartbeatSent(now: number = Date.now()): void {
  const record = readRecord();
  if (!record) return;
  writeRecord({ ...record, lastHeartbeatWall: now });
}

export function isTracking(): boolean {
  return readRecord() !== null;
}

/**
 * Has the user interacted within the last `withinMs`?
 *
 * Used by apiClient's request interceptor to stamp `X-User-Activity` on requests
 * that follow real interaction, so most renewals ride along on calls the app was
 * making anyway and a single dropped heartbeat can't expire an active user.
 *
 * The window must stay SHORT (a few seconds). Widen it and background polls —
 * which fire every 5s app-wide — would keep renewing for the whole window after
 * the user walked away, which is precisely the failure this feature exists to
 * prevent.
 */
export function hadRecentActivity(withinMs: number = ACTIVITY_WRITE_THROTTLE_MS, now: number = Date.now()): boolean {
  const record = readRecord();
  if (!record) return false;

  return now - record.lastActivityWall <= withinMs;
}

// ─── Activity holds ──────────────────────────────────────────────────────────

/**
 * Keep the session alive while something is happening that produces no input
 * events — a connected Twilio video/voice call, or note dictation.
 *
 * Without this a clinician on a 15-minute video visit, or dictating a long note,
 * generates no pointerdown/keydown/wheel/scroll at all and gets signed out
 * mid-task, losing the recording. It IS a deliberate policy exception to
 * "activity means input", which is why it's bounded: memory-only so it can never
 * survive a reload, released on unmount, and capped by HOLD_CEILING_MS so a hold
 * leaked by a buggy component can't grant an immortal session.
 */
export function hold(tag: string): void {
  if (holds.size === 0) holdsSince = Date.now();
  holds.add(tag);
}

export function release(tag: string): void {
  holds.delete(tag);
  if (holds.size === 0) holdsSince = null;
}

export function hasActiveHolds(now: number = Date.now()): boolean {
  if (holds.size === 0) return false;
  if (holdsSince !== null && now - holdsSince > HOLD_CEILING_MS) return false;
  return true;
}

/** Test-only: reset all module state between cases. */
export function resetIdleSessionForTests(): void {
  clearIdleRecord();
}

export const IDLE_RECORD_KEY = RECORD_KEY;
export const IDLE_SAFETY_MARGIN_MS = SAFETY_MARGIN_MS;
export const IDLE_ACTIVITY_THROTTLE_MS = ACTIVITY_WRITE_THROTTLE_MS;
export const IDLE_HOLD_CEILING_MS = HOLD_CEILING_MS;
